import { useCallback, useMemo } from "react";

import {
  NumericStringFilterType,
  TextStringFilterType,
  TransitionProposalHoldingsFilterResponse,
} from "@fartherfinance/frontend/api/TransitionProposals/types";

export const useFilterTypeData = (
  filters: TransitionProposalHoldingsFilterResponse,
  appliedFilter: TextStringFilterType | NumericStringFilterType | undefined,
  filterSearchTerm: string | undefined,
  activeFilters: TransitionProposalHoldingsFilterResponse
) => {
  const [checkBoxFilters, numericFilters] = useMemo(
    () => [
      {
        type: filters.type,
        name: filters.name,
        fileName: filters.fileName,
        accountName: filters.accountName,
        statementPeriod: filters.statementPeriod,
        ticker: filters.ticker,
      },
      {
        costBasis: filters.costBasis,
        price: filters.price,
        quantity: filters.quantity,
        marketValue: filters.marketValue,
        unrealizedGainOrLoss: filters.unrealizedGainOrLoss,
        percentOfAccount: filters.percentOfAccount,
      },
    ],
    [filters]
  );

  const isCheckboxFilter = useCallback(
    (filter: string | undefined): filter is TextStringFilterType =>
      filter !== undefined && filter in checkBoxFilters,
    [checkBoxFilters]
  );

  const isSliderFilter = useCallback(
    (filter: string | undefined): filter is NumericStringFilterType =>
      filter !== undefined && filter in numericFilters,
    [numericFilters]
  );

  const checkBoxDropdownFilterList = useMemo(() => {
    if (!isCheckboxFilter(appliedFilter)) {
      return [];
    }

    const values = checkBoxFilters[appliedFilter];

    const uniqueData = Array.from(new Set(values));

    const lowerSearch = (filterSearchTerm ?? "").trim().toLowerCase();

    const data = lowerSearch
      ? uniqueData.filter((item) => item.toLowerCase().startsWith(lowerSearch))
      : uniqueData;

    return data.sort((a, b) => {
      const isAChecked = activeFilters[appliedFilter]?.includes(a) ?? false;
      const isBChecked = activeFilters[appliedFilter]?.includes(b) ?? false;

      return isAChecked === isBChecked
        ? a.localeCompare(b)
        : isAChecked
        ? -1
        : 1;
    });
  }, [
    appliedFilter,
    filterSearchTerm,
    activeFilters,
    checkBoxFilters,
    isCheckboxFilter,
  ]);

  const sliderDropdownFilterRange: {
    min: number;
    max: number;
    value: [number, number];
  } = useMemo(() => {
    if (!isSliderFilter(appliedFilter)) {
      return { min: 0, max: 0, value: [0, 0] };
    }

    const minMax = filters[appliedFilter];

    const [min, max] = minMax;

    const rangeMin = Number(min);
    const rangeMax = Number(max);

    const activeRangeValue = activeFilters[appliedFilter];

    const isValidTuple =
      activeRangeValue &&
      activeRangeValue.length === 2 &&
      activeRangeValue.every((val) => typeof val === "number");

    const active: [number, number] = isValidTuple
      ? [Number(activeRangeValue[0]), Number(activeRangeValue[1])]
      : [rangeMin, rangeMax];

    return {
      min: rangeMin,
      max: rangeMax,
      value: active,
    };
  }, [appliedFilter, isSliderFilter, activeFilters, filters]);

  return {
    checkBoxDropdownFilterList,
    sliderDropdownFilterRange,
  };
};
